/* TradePro locale-preserving internal routing v22.14C + separator recovery v22.14F + malformed-return repair v22.14G */
(() => {
  const rawRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root
    : '/';

  const normalizeRoot = (value) => {
    const candidate = String(value || '/').trim();
    const prefixed = candidate.startsWith('/') ? candidate : `/${candidate}`;
    const singleSlashes = prefixed.replace(/\/{2,}/g, '/');
    return singleSlashes.endsWith('/') ? singleSlashes : `${singleSlashes}/`;
  };

  const rootPath = normalizeRoot(rawRoot);
  const rootPrefix = rootPath === '/' ? '' : rootPath.slice(0, -1);
  const compactLocalePrefix = rootPrefix.replace(/^\/+/, '');
  const malformedLocalePrefix = compactLocalePrefix
    ? `/${compactLocalePrefix}${compactLocalePrefix}`
    : '';
  const internalRoots = new Set([
    'products',
    'collections',
    'pages',
    'blogs',
    'search',
    'cart',
    'account',
    'policies',
    'discount'
  ]);

  const canRewritePath = (pathname) => {
    if (rootPath === '/') return false;
    if (pathname === rootPrefix || pathname.startsWith(rootPath)) return false;
    if (pathname === '/') return true;

    const firstSegment = pathname.replace(/^\/+/, '').split('/')[0];
    return internalRoots.has(firstSegment);
  };

  const localizedHref = (rawValue) => {
    if (!rawValue || typeof rawValue !== 'string') return rawValue;

    const trimmed = rawValue.trim();
    if (!trimmed || trimmed.startsWith('#')) return rawValue;
    if (/^(?:mailto:|tel:|sms:|javascript:|data:)/i.test(trimmed)) return rawValue;

    let resolved;
    try {
      resolved = new URL(trimmed, window.location.origin);
    } catch (error) {
      return rawValue;
    }

    if (resolved.origin !== window.location.origin) return rawValue;

    let recoveredMalformedLocalePrefix = false;

    if (
      malformedLocalePrefix &&
      (resolved.pathname === malformedLocalePrefix ||
        resolved.pathname.startsWith(`${malformedLocalePrefix}/`))
    ) {
      const remainder = resolved.pathname
        .slice(malformedLocalePrefix.length)
        .replace(/^\/+/, '');
      resolved.pathname = remainder ? `${rootPath}${remainder}` : rootPath;
      recoveredMalformedLocalePrefix = true;
    }

    if (recoveredMalformedLocalePrefix) {
      return `${resolved.pathname}${resolved.search}${resolved.hash}`;
    }

    if (!canRewritePath(resolved.pathname)) return rawValue;

    const relativePath = resolved.pathname.replace(/^\/+/, '');
    resolved.pathname = relativePath ? `${rootPath}${relativePath}` : rootPath;

    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  };

  const normalizeAnchor = (anchor) => {
    if (!anchor || anchor.dataset.tradeproLocaleRoutingDone === 'true') return;

    const target = (anchor.getAttribute('target') || '').toLowerCase();
    if (target && target !== '_self') return;
    if (anchor.hasAttribute('download')) return;

    const current = anchor.getAttribute('href');
    const next = localizedHref(current);

    if (next && next !== current) {
      anchor.setAttribute('href', next);
    }

    anchor.dataset.tradeproLocaleRoutingDone = 'true';
  };

  const normalizeForm = (form) => {
    if (!form || form.dataset.tradeproLocaleRoutingDone === 'true') return;

    const current = form.getAttribute('action');
    const next = localizedHref(current);

    if (next && next !== current) {
      form.setAttribute('action', next);
    }

    form.dataset.tradeproLocaleRoutingDone = 'true';
  };

  const normalizeNode = (node) => {
    if (!(node instanceof Element)) return;

    if (node.matches('a[href]')) normalizeAnchor(node);
    if (node.matches('form[action]')) normalizeForm(node);

    node.querySelectorAll?.('a[href]').forEach(normalizeAnchor);
    node.querySelectorAll?.('form[action]').forEach(normalizeForm);
  };

  const normalizeDocument = () => {
    document.querySelectorAll('a[href]').forEach(normalizeAnchor);
    document.querySelectorAll('form[action]').forEach(normalizeForm);
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!anchor) return;

    anchor.dataset.tradeproLocaleRoutingDone = '';
    normalizeAnchor(anchor);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    form.dataset.tradeproLocaleRoutingDone = '';
    normalizeForm(form);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', normalizeDocument, { once: true });
  } else {
    normalizeDocument();
  }

  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach(normalizeNode);
    });
  });

  const observeRoot = () => {
    if (!document.body) return;
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.body) {
    observeRoot();
  } else {
    document.addEventListener('DOMContentLoaded', observeRoot, { once: true });
  }
})();

